import { useState } from 'react';
import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  backLinkVariants,
  Badge,
  Button,
  buttonVariants,
  Card,
  ConfirmDialog,
  EmptyState,
  HeroActions,
  HeroBar,
  HeroBody,
  HeroMeta,
  HeroTitle,
  IconButton,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  PageBody,
  SettingsRow,
  SettingsSection,
  SettingsValue,
  Spinner,
} from '@wundo/ui';
import { ChevronLeft, Clock, EllipsisVertical, Trash2 } from 'lucide-react';
import { ApiError } from '@wundo/client/api.ts';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import {
  deleteOrganisation,
  getOrganisation,
  ORGANISATION_FEATURE_DEFINITIONS,
  type OrganisationDetail as OrganisationRecord,
  organisationFeatureLabel,
  organisationTypeLabel,
} from '@/lib/organisations.ts';
import { useRecordOrganisation } from '@/lib/recordOrganisation.ts';
import { apiErrorMessage } from '@/lib/apiErrorMessage.ts';
import { fetchCohort } from '@/lib/cohorts.ts';
import { formatDate } from '@/utils/dateUtils.ts';
import { OrganisationTypeBadge } from '@/components/organisations/OrganisationTypeBadge.tsx';
import { GuestVisitDialog } from '@/components/organisations/GuestVisitDialog.tsx';
import type { OrganisationFeatures } from '@wundo/shared/organisationFeature.ts';

export const Route = createFileRoute('/_app/organisations/$id/')({
  staticData: { orgScope: 'record', orgScopeList: '/organisations' },
  component: OrganisationDetailPage,
});

export function OrganisationDetailPage() {
  const { id } = Route.useParams();

  return <OrganisationDetail organisationId={id} />;
}

export function OrganisationDetail({ organisationId }: { organisationId: string }) {
  useRecordOrganisation(organisationId);
  const router = useRouter();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [visiting, setVisiting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });
  const superadmin = currentUser?.role === 'superadmin';

  const {
    data: organisation,
    isPending,
    error,
  } = useQuery({
    queryKey: ['organisation', organisationId],
    queryFn: () => getOrganisation(organisationId),
  });

  const remove = useMutation({
    mutationFn: () => deleteOrganisation(organisationId),
    onSuccess: async () => {
      setConfirming(false);
      await queryClient.invalidateQueries({ queryKey: ['organisations'] });
      void router.navigate({ to: '/organisations' });
    },
    onError: (caught: unknown) => {
      setDeleteError(
        apiErrorMessage(
          caught,
          caught instanceof ApiError && caught.status === 422
            ? 'This organisation has users and cannot be deleted.'
            : 'Failed to delete organisation',
        ),
      );
    },
  });

  if (isPending) {
    return (
      <PageBody flush>
        <Card>
          <p className='flex items-center gap-2 text-sm font-semibold text-ink-muted'>
            <Spinner />
            Loading…
          </p>
        </Card>
      </PageBody>
    );
  }

  if (error) {
    const missing = error instanceof ApiError && error.status === 404;

    return (
      <PageBody flush>
        <Card>
          <EmptyState
            title={
              missing ? 'Organisation not found' : 'Failed to load organisation details'
            }
            description={
              missing
                ? 'It may have been deleted since this link was made.'
                : 'Try again in a moment.'
            }
            action={
              <Link
                to='/organisations'
                className={buttonVariants({ size: 'sm', variant: 'quiet' })}
              >
                Back to organisations
              </Link>
            }
          />
        </Card>
      </PageBody>
    );
  }

  return (
    <>
      {/* Above the hero bar, in the space PageBody normally leaves for a title. */}
      <Link to='/organisations' className={backLinkVariants({ className: 'mb-2.5' })}>
        <ChevronLeft aria-hidden='true' />
        Organisations
      </Link>

      {/* `@container`: the two sections fold on the column they are in, not the window. */}
      <PageBody flush className='@container flex flex-col gap-4'>
        <HeroBar>
          <HeroBody>
            <HeroTitle>{organisation.name}</HeroTitle>
            <HeroMeta>{organisationTypeLabel(organisation.type)}</HeroMeta>
          </HeroBody>
          <HeroActions>
            {superadmin && (
              <Button variant='onPrimary' size='sm' onClick={() => setVisiting(true)}>
                Work in this organisation
              </Button>
            )}
            <Link
              to='/organisations/$id/edit'
              params={{ id: organisation.id }}
              className={buttonVariants({ variant: 'onPrimary', size: 'sm' })}
            >
              Edit
            </Link>
            <OrganisationActionsMenu
              organisationId={organisation.id}
              superadmin={superadmin}
              onDelete={() => {
                setDeleteError('');
                setConfirming(true);
              }}
            />
          </HeroActions>
        </HeroBar>

        {/*
          Two lists of the same kind: beside each other on a console-width page,
          stacked again as soon as one column would be too narrow to read.
        */}
        <div className='grid grid-cols-2 items-start gap-4 @max-agenda:grid-cols-1'>
          <OrganisationDetailsSection organisation={organisation} />
          <OrganisationFeaturesOverview features={organisation.features} />
        </div>
      </PageBody>

      <GuestVisitDialog
        organisationId={organisation.id}
        organisationName={organisation.name}
        open={visiting}
        onOpenChange={setVisiting}
      />

      <ConfirmDialog
        open={confirming}
        onOpenChange={(open) => {
          if (!open) {
            setConfirming(false);
            setDeleteError('');
          }
        }}
        title='Delete organisation?'
        description={`Are you sure you want to delete organisation ${organisation.name}? This action cannot be undone.`}
        confirmLabel='Delete'
        cancelLabel='Cancel'
        variant='danger'
        error={deleteError === '' ? undefined : deleteError}
        onConfirm={() => remove.mutateAsync().catch(() => undefined)}
      />
    </>
  );
}

interface OrganisationActionsMenuProps {
  organisationId: string;
  superadmin: boolean;
  onDelete: () => void;
}

function OrganisationActionsMenu({
  organisationId,
  superadmin,
  onDelete,
}: OrganisationActionsMenuProps) {
  const router = useRouter();

  return (
    <Menu>
      <MenuTrigger
        render={
          <IconButton aria-label='Organisation actions' size='sm' variant='surface' />
        }
      >
        <EllipsisVertical className='size-4' aria-hidden='true' />
      </MenuTrigger>
      <MenuContent align='end'>
        {superadmin && (
          <MenuItem
            onClick={() =>
              void router.navigate({
                href: `/admin/change-logs?entity_type=organisation&entity_id=${organisationId}`,
              })
            }
          >
            <Clock />
            Change Logs
          </MenuItem>
        )}
        <MenuItem tone='danger' onClick={onDelete}>
          <Trash2 />
          Delete
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}

function OrganisationDetailsSection({
  organisation,
}: {
  organisation: OrganisationRecord;
}) {
  const cohortId = organisation.default_cohort_id ?? null;
  const { data: defaultCohort } = useQuery({
    queryKey: ['cohort', cohortId],
    queryFn: () => fetchCohort(cohortId as string),
    enabled: cohortId !== null,
  });

  return (
    <SettingsSection title='Details' className='mb-0'>
      <SettingsRow label='Name'>
        <SettingsValue>{organisation.name}</SettingsValue>
      </SettingsRow>
      <SettingsRow label='Type'>
        <OrganisationTypeBadge type={organisation.type} />
      </SettingsRow>
      <SettingsRow label='Microsoft Teams Tenant ID'>
        <SettingsValue>
          {organisation.msteams_tenant_id || 'Not configured'}
        </SettingsValue>
      </SettingsRow>
      <SettingsRow label='Slack team ID'>
        <SettingsValue>{organisation.slack_team_id || 'Not configured'}</SettingsValue>
      </SettingsRow>
      <SettingsRow label='Default Cohort'>
        {defaultCohort ? (
          <Link
            to='/cohorts/$id'
            params={{ id: defaultCohort.id }}
            className='text-sm font-semibold text-ink hover:underline'
          >
            {defaultCohort.title}
          </Link>
        ) : (
          <SettingsValue>Not configured</SettingsValue>
        )}
      </SettingsRow>
      <SettingsRow label='Created'>
        <SettingsValue>{formatDate(organisation.created_at)}</SettingsValue>
      </SettingsRow>
      <SettingsRow label='Last Updated'>
        <SettingsValue>{formatDate(organisation.updated_at)}</SettingsValue>
      </SettingsRow>
    </SettingsSection>
  );
}

function OrganisationFeaturesOverview({ features }: { features: OrganisationFeatures }) {
  return (
    <SettingsSection title='Features' className='mb-0'>
      {ORGANISATION_FEATURE_DEFINITIONS.map((feature) => {
        const set = features?.[feature.key];

        return (
          <SettingsRow
            key={feature.key}
            label={organisationFeatureLabel(feature.key)}
            description={feature.description}
          >
            {set === undefined ? (
              <SettingsValue>Default</SettingsValue>
            ) : (
              <Badge tone={set ? 'success' : 'neutral'}>
                {set ? 'Enabled' : 'Disabled'}
              </Badge>
            )}
          </SettingsRow>
        );
      })}
    </SettingsSection>
  );
}
